import './App.css';
import { useEffect } from 'react';
import { Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { captureLanding, trackPageView } from './lib/analytics';
import { BookingProvider } from './context/BookingContext';
import { PartnerAuthProvider } from './context/PartnerAuthContext';
import { PartnerBookingProvider } from './context/PartnerBookingContext';
import Header from './components/shared/Header';
import CookieNotice from './components/shared/CookieNotice';
import BoardingNotice from './components/shared/BoardingNotice';
import Login from './screens/Login';
import OtpVerification from './screens/OtpVerification';
import SelectDate from './screens/SelectDate';
import Enquiry from './screens/Enquiry';
import SelectPassengers from './screens/SelectPassengers';
import Availability from './screens/Availability';
import CruiseSummary from './screens/CruiseSummary';
import PassengerDetails from './screens/PassengerDetails';
import TermsAndConditions from './screens/TermsAndConditions';
import Payment from './screens/Payment';
import Confirmation from './screens/Confirmation';
import TableLink from './screens/TableLink';
import PartnerLogin from './screens/partner/PartnerLogin';
import PartnerRegister from './screens/partner/PartnerRegister';
import PartnerForgotPassword from './screens/partner/PartnerForgotPassword';
import PartnerResetPassword from './screens/partner/PartnerResetPassword';
import PartnerSetPassword from './screens/partner/PartnerSetPassword';
import PartnerDashboard from './screens/partner/PartnerDashboard';
import PartnerSelectAgent from './screens/partner/PartnerSelectAgent';
import PartnerSelectDate from './screens/partner/PartnerSelectDate';
import PartnerSelectPassengers from './screens/partner/PartnerSelectPassengers';
import PartnerAvailability from './screens/partner/PartnerAvailability';
import PartnerCruiseSummary from './screens/partner/PartnerCruiseSummary';
import PartnerPassengerDetails from './screens/partner/PartnerPassengerDetails';
import PartnerTerms from './screens/partner/PartnerTerms';
import PartnerPayment from './screens/partner/PartnerPayment';
import PartnerConfirmation from './screens/partner/PartnerConfirmation';
import PartnerBookings from './screens/partner/PartnerBookings';
import PartnerProfile from './screens/partner/PartnerProfile';

// Logo is the hero element only on the entry routes; it shrinks elsewhere so content leads
const HERO_ROUTES = ['/', '/verify'];
// Routes that break out of the narrow card into a wider, top-aligned layout
const WIDE_ROUTES = ['/availability', '/cruise', '/passenger-details', '/terms', '/payment', '/confirmation', '/partner/book/availability', '/partner/book/cruise', '/partner/book/passenger-details', '/partner/book/terms', '/partner/book/payment', '/partner/book/confirmation', '/partner/bookings', '/partner/profile'];

// Persistent page shell — the logo + diamond divider live here, outside <Routes>,
// so they never remount (and never replay their entry animation) during navigation.
// Only the routed screen content below the Header transitions.
function Shell() {
  const { pathname } = useLocation();
  const heroLogo = HERO_ROUTES.includes(pathname);
  // /t/:token (staff-issued table booking links) is dynamic, so it is matched
  // by prefix rather than listed.
  const wideStep = WIDE_ROUTES.includes(pathname) || pathname.startsWith('/t/');
  const isCruiseSummary = pathname === '/cruise' || pathname === '/partner/book/cruise';

  // Meta Pixel PageView per client-side route change (the document load is
  // reported by public/index.html and skipped here), plus a one-off capture of
  // the landing URL's campaign parameters. Partner routes are excluded inside
  // trackPageView. GA4 is untouched: its funnel is the named events.
  useEffect(() => {
    captureLanding();
  }, []);
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  // The boarding-point notice strip (when one is due) sits above the shell in
  // normal flow and publishes its height as --ag-notice-h; the shell's
  // viewport-height sizing subtracts it so no screen gains a scrollbar.
  return (
    <>
      <BoardingNotice />
      <div
        className={`min-h-[calc(100vh_-_var(--ag-notice-h,0px))] w-full flex flex-col items-center px-4 pb-8 ${heroLogo ? 'pt-8' : 'pt-4'} bg-luxury ${wideStep ? 'justify-start' : 'justify-center'} ${isCruiseSummary ? 'md:h-[calc(100vh_-_var(--ag-notice-h,0px))] md:overflow-hidden' : ''}`}
      >
        <Header size={heroLogo ? 'hero' : 'compact'} />

        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/verify" element={<OtpVerification />} />
          <Route path="/select-date" element={<SelectDate />} />
          <Route path="/enquiry" element={<Enquiry />} />
          <Route path="/passengers" element={<SelectPassengers />} />
          <Route path="/availability" element={<Availability />} />
          <Route path="/cruise" element={<CruiseSummary />} />
          <Route path="/passenger-details" element={<PassengerDetails />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/confirmation" element={<Confirmation />} />

          {/* Staff-issued table booking link (no OTP — the link itself is the
              verification). Reads nothing with the anon key; see TableLink.js. */}
          <Route path="/t/:token" element={<TableLink />} />

          {/* Partner portal — a separate Supabase Auth system, isolated from the
              customer OTP flow above. See PartnerAuthContext / supabasePartner. */}
          <Route path="/partner" element={<PartnerDashboard />} />
          <Route path="/partner/bookings" element={<PartnerBookings />} />
          <Route path="/partner/profile" element={<PartnerProfile />} />
          <Route path="/partner/login" element={<PartnerLogin />} />
          <Route path="/partner/register" element={<PartnerRegister />} />
          <Route path="/partner/forgot" element={<PartnerForgotPassword />} />
          <Route path="/partner/reset" element={<PartnerResetPassword />} />
          <Route path="/partner/set-password" element={<PartnerSetPassword />} />

          {/* Partner self-service booking flow — a SEPARATE flow from the customer
              booking routes above, running on its own PartnerBookingProvider. The
              provider wraps only this /partner/book/* subtree (via a layout route
              + Outlet), so it never touches the customer flow or the partner auth
              routes. Only approved partners reach these screens (guarded in-screen). */}
          <Route
            path="/partner/book"
            element={
              <PartnerBookingProvider>
                <Outlet />
              </PartnerBookingProvider>
            }
          >
            <Route path="agent" element={<PartnerSelectAgent />} />
            <Route path="date" element={<PartnerSelectDate />} />
            <Route path="passengers" element={<PartnerSelectPassengers />} />
            <Route path="availability" element={<PartnerAvailability />} />
            <Route path="cruise" element={<PartnerCruiseSummary />} />
            <Route path="passenger-details" element={<PartnerPassengerDetails />} />
            <Route path="terms" element={<PartnerTerms />} />
            <Route path="payment" element={<PartnerPayment />} />
            <Route path="confirmation" element={<PartnerConfirmation />} />
          </Route>
        </Routes>

        {/* GA4 cookie consent — shared choice with www.alfrescogrand.com */}
        <CookieNotice />
      </div>
    </>
  );
}

function App() {
  // PartnerAuthProvider is an independent provider for the partner portal only.
  // It does not read or write any BookingContext / customer-flow state, so the
  // customer OTP flow is unaffected by wrapping the whole app with it.
  return (
    <PartnerAuthProvider>
      <BookingProvider>
        <Shell />
      </BookingProvider>
    </PartnerAuthProvider>
  );
}

export default App;
